import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
    AppBar,
    DialogActions,
    DialogContent,
    MenuItem,
    Select,
    SelectChangeEvent,
    Toolbar,
    Typography,
    IconButton,
    Button,
    Box,
    FormControl,
    InputLabel,
    Tooltip,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from 'store';
import { setSelectedProjectShortName } from 'store/additional/projectFilter';
import { GetProjectApiResponse } from 'store/userOrg/userOrgApi';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import _ from 'lodash';
import {
    useGetApiUserPreferencesParsedQuery,
    usePostApiRequestsMutation,
    usePutApiUserPreferencesMutation,
} from 'store/userOrg/enhancedUserOrgApi';
import { toast } from 'react-toastify';
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from 'utils/constants';
import { Dialog } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { FormInputText } from 'components/reactHookForm/FormInputText';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { RequestType } from 'helpers/enums/requestType';

interface GlobalFilterProps {
    projects: GetProjectApiResponse | undefined;
}
const DEFAULT_ACCESS_VALUES = { shortName: '' };

const WORKFLOW_ROUTE_REGEX = /^\/workflows\/[^/]+/;
const DATA_ROUTE_REGEX = /^\/data\/[^/]+/;
const WORKFLOW_DATA_SETS_ROUTE_REGEX = /^\/workflowDataSets\/[^/]+/;

const GlobalFilter = ({ projects }: GlobalFilterProps) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const [putPreferences] = usePutApiUserPreferencesMutation();
    const [createRequest] = usePostApiRequestsMutation();

    const { data: preferences } = useGetApiUserPreferencesParsedQuery();
    const { selectedProjectShortName } = useSelector((state: RootState) => state.projectFilter);
    const prevShortNameRef = useRef<string | null>(null);

    const [accessRequestOpen, setAccessRequestOpen] = useState(false);
    const accessMethods = useForm({ defaultValues: DEFAULT_ACCESS_VALUES });

    const isOnWorkflowRoute = WORKFLOW_ROUTE_REGEX.test(location.pathname);
    const isOnDataRoute = DATA_ROUTE_REGEX.test(location.pathname);
    const isOnWorkflowDataSetsRoute = WORKFLOW_DATA_SETS_ROUTE_REGEX.test(location.pathname);

    const isFilterDisabled = isOnWorkflowRoute || isOnDataRoute || isOnWorkflowDataSetsRoute;

    const handleChange = (event: SelectChangeEvent<string>, _: ReactNode) => {
        if (isOnWorkflowRoute || isOnDataRoute || isOnWorkflowDataSetsRoute) return;

        const newProjectShortName = event.target.value;
        dispatch(setSelectedProjectShortName(newProjectShortName));

        const selectedProject = sortedProjects.find(p => p.ShortName === newProjectShortName);
        const displayName = selectedProject?.Name || newProjectShortName;

        toast.success(`Switched to project: ${displayName}`, {
            autoClose: 2000,
            position: 'top-center',
        });
    };

    const sortedProjects = useMemo(() => {
        const activeProjects = projects?.filter(project => {
            const isActive = !project.EndDate || new Date(project.EndDate) >= new Date();
            const isNotCypress = !project.ShortName?.toLowerCase().includes('cypress');
            return isActive && isNotCypress;
        });
        return _.sortBy(activeProjects, [project => project.ShortName?.toLowerCase()]);
    }, [projects]);

    const hasNoProjects = Array.isArray(projects) && projects.length === 0;
    const hasNoActiveProjects = !hasNoProjects && sortedProjects.length === 0;

    useEffect(() => {
        if (!sortedProjects || sortedProjects.length === 0) {
            toast.error(ERROR_MESSAGES.ERROR_PROJECTS);
            return;
        }

        let newDefaultShortName: string | undefined = undefined;

        const preferredProjectShortName = preferences?.builderProject;
        if (preferredProjectShortName) {
            const isPreferredProjectValid = sortedProjects.some(
                project => project.ShortName === preferredProjectShortName
            );
            if (isPreferredProjectValid) {
                newDefaultShortName = preferredProjectShortName;
            }
        }

        if (!newDefaultShortName && sortedProjects[0]?.ShortName) {
            newDefaultShortName = sortedProjects[0].ShortName;
        }

        if (!selectedProjectShortName && newDefaultShortName) {
            dispatch(setSelectedProjectShortName(newDefaultShortName));
        }
    }, [sortedProjects, preferences, dispatch, selectedProjectShortName]);

    useEffect(() => {
        if (!selectedProjectShortName || !preferences || sortedProjects.length === 0) return;

        if (prevShortNameRef.current === selectedProjectShortName) return;

        if (prevShortNameRef.current === null && preferences.builderProject === selectedProjectShortName) {
            prevShortNameRef.current = selectedProjectShortName;
            return;
        }

        prevShortNameRef.current = selectedProjectShortName;

        putPreferences({
            systemTextJsonJsonElement: {
                ...(preferences && typeof preferences === 'object' ? preferences : {}),
                builderProject: selectedProjectShortName,
            },
        });
    }, [selectedProjectShortName, preferences, putPreferences, sortedProjects.length]);

    const handleAccessSubmit = async (data: { shortName: string }) => {
        await createRequest({
            userOrganizationManagementDomainExtModelsUserOrgInputModelsProjectRequestCreate: {
                ProjectShortName: data.shortName,
                RequestType: RequestType[1],
                ContactEmail: null,
                ProjectResourceName: null,
                ProviderId: null,
            },
        }).unwrap();
        toast.success(SUCCESS_MESSAGES.REQUEST);
        setAccessRequestOpen(false);
        accessMethods.reset();
    };

    if (hasNoProjects || hasNoActiveProjects) {
        return (
            <Box id='globalFilter'>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 0.9,
                        borderRadius: 1,
                        backgroundColor: 'warning.light',
                        border: '0.5px solid',
                        borderColor: 'warning.main',
                    }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: 'warning.dark', flexShrink: 0 }} />
                    <Typography variant='body2' sx={{ color: 'text.primary', lineHeight: 1.5 }}>
                        {hasNoProjects ? 'You are not assigned to any project yet.' : 'No active projects available.'}{' '}
                        <Box
                            component='span'
                            onClick={() => setAccessRequestOpen(true)}
                            sx={{
                                textDecoration: 'underline',
                                textUnderlineOffset: '2px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.75 },
                            }}>
                            Request project access →
                        </Box>
                    </Typography>
                </Box>

                <Dialog
                    open={accessRequestOpen}
                    onClose={() => {
                        setAccessRequestOpen(false);
                        accessMethods.reset();
                    }}
                    fullWidth
                    scroll='paper'>
                    <AppBar sx={{ position: 'relative', backgroundColor: 'primary.main' }}>
                        <Toolbar>
                            <Typography sx={{ flex: 1 }} variant='h5' component='div'>
                                Request project access
                            </Typography>
                            <IconButton
                                edge='end'
                                color='inherit'
                                onClick={() => {
                                    setAccessRequestOpen(false);
                                    accessMethods.reset();
                                }}
                                aria-label='close dialog'>
                                <CloseIcon />
                            </IconButton>
                        </Toolbar>
                    </AppBar>
                    <DialogContent dividers>
                        <FormProvider {...accessMethods}>
                            <FormInputText
                                name='shortName'
                                required
                                label='Short Name'
                                margin='normal'
                                fullWidth
                                helperText='Short Name is required'
                            />
                        </FormProvider>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            color='inherit'
                            variant='outlined'
                            onClick={() => {
                                setAccessRequestOpen(false);
                                accessMethods.reset();
                            }}
                            sx={{ minWidth: 100 }}>
                            Cancel
                        </Button>
                        <LoadingButton
                            color='primary'
                            variant='contained'
                            sx={{ minWidth: 100 }}
                            onClick={accessMethods.handleSubmit(handleAccessSubmit)}>
                            Submit
                        </LoadingButton>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    }

    let projectChangeTooltip = '';

    if (isOnWorkflowRoute) {
        projectChangeTooltip = 'Project cannot be changed while editing a workflow.';
    } else if (isOnDataRoute || isOnWorkflowDataSetsRoute) {
        projectChangeTooltip = 'Project cannot be changed while managing dataset.';
    }

    if (!selectedProjectShortName) {
        return null;
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} id='globalFilter'>
            <Tooltip
                title={projectChangeTooltip}
                arrow
                disableHoverListener={!isFilterDisabled}
                disableFocusListener={!isFilterDisabled}
                disableTouchListener={!isFilterDisabled}>
                <span>
                    <FormControl variant='outlined' size='small' sx={{ minWidth: 200 }} disabled={isFilterDisabled}>
                        {' '}
                        <InputLabel id='project-filter-label'>Active Project</InputLabel>
                        <Select
                            labelId='project-filter-label'
                            id='project-filter'
                            onChange={handleChange}
                            value={selectedProjectShortName}
                            label='Active Project'
                            data-testid='project-filter'
                            startAdornment={
                                isFilterDisabled ? (
                                    <LockOutlinedIcon fontSize='small' sx={{ mr: 1, color: 'text.disabled' }} />
                                ) : (
                                    <FilterListIcon fontSize='small' sx={{ mr: 1, color: 'primary.main' }} />
                                )
                            }
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        maxHeight: 400,
                                        '& .MuiMenuItem-root': {
                                            '&:hover': { backgroundColor: 'action.hover' },
                                        },
                                    },
                                },
                            }}>
                            {sortedProjects.map(project => {
                                const isSelected = project.ShortName === selectedProjectShortName;
                                return (
                                    <MenuItem
                                        key={project.ShortName || 'unknown'}
                                        value={project.ShortName || ''}
                                        selected={isSelected}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                                gap: 1,
                                            }}>
                                            <span>{project.Name}</span>
                                            {isSelected && (
                                                <CheckCircleIcon
                                                    fontSize='small'
                                                    sx={{
                                                        color: isFilterDisabled ? 'text.disabled' : 'primary.main',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </span>
            </Tooltip>
        </Box>
    );
};

export default GlobalFilter;
